import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { Icon } from 'components/Atoms/Icon/Icon';
import { ResidentCustomPublicationConsent } from 'types/publishedAssets';
import { SignupDisclaimer } from './SignupDisclaimer';
import { useNotificationSignupSubscribe } from './useNotificationSignupSubscribe';
import styles from './NotificationSignup.module.scss';

interface Props {
  email?: string;
  missingPublicationIds: string[];
  // Contexts without a survey Next button (standalone page, end screen) need an
  // explicit submit button: the checkbox becomes consent and subscribe only
  // fires on submit. Survey regular screens leave this off and subscribe on
  // first check.
  showSubmitButton?: boolean;
  buttonLabel?: string;
  consents?: ResidentCustomPublicationConsent[];
}

// Authenticated resident opt-in row (residents not yet on every configured
// list — the already-subscribed case renders <AlreadySubscribed/> instead).
// On survey regular screens the checkbox subscribes on first check; on
// page/end-screen contexts (showSubmitButton) the checkbox is consent and an
// explicit submit button drives subscribe. Opt-in is one-way within the same
// session — once subscribed it stays checked until the preferences query
// refetches (which the subscribe hook invalidates).
export function AuthenticatedOptIn({
  email,
  missingPublicationIds,
  showSubmitButton = false,
  buttonLabel,
  consents,
}: Props) {
  const { t } = useTranslation();
  const { subscribe } = useNotificationSignupSubscribe(missingPublicationIds);
  const [checked, setChecked] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Survey regular screens: the checkbox is the trigger — subscribe on first check.
  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.checked || !email || checked) return;
    setChecked(true);
    await subscribe(email);
  };

  // Page / end-screen: the explicit button is the consent + trigger.
  const handleSubmit = async () => {
    if (!email || hasSubmitted) return;
    await subscribe(email);
    setHasSubmitted(true);
  };

  const optInText = (
    <span className={styles.optInText}>
      {t('notificationSignup.optInPrefix')} <strong>{email}</strong>.
    </span>
  );

  const disclaimer = <SignupDisclaimer html={t('notificationSignup.authDisclaimer')} consents={consents} />;

  // Logged-in, page/end-screen: no checkbox — the email line is static and the
  // Subscribe button below it is the consent + trigger.
  if (showSubmitButton) {
    return (
      <>
        <div className={styles.optIn}>{optInText}</div>
        {hasSubmitted ? (
          <div className={styles.noticeBox}>
            <Icon name="info" />
            <p>{t('notificationSignup.subscribedNotice')}</p>
          </div>
        ) : (
          <ActionButton type={ButtonType.ACTION} onClick={handleSubmit} className={styles.submitButton}>
            {buttonLabel}
          </ActionButton>
        )}
        {disclaimer}
      </>
    );
  }

  return (
    <>
      <label className={classNames(styles.optIn, { [styles.optInChecked]: checked })}>
        <input
          type="checkbox"
          className={styles.optInCheckbox}
          checked={checked}
          disabled={checked}
          onChange={handleCheckboxChange}
          aria-label={t('notificationSignup.optInPrefix')}
        />
        {optInText}
      </label>
      {disclaimer}
    </>
  );
}
